import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MatrixTable, type MatrixColumn, type MatrixSection } from '@/components/ui';
import type { MatrixCell } from '../RoleCoverage/types';
import {
    RoleMatrixCell,
    RoleMatrixColumnHeader,
    RoleMatrixRowHeader,
} from '../RoleCoverage/RoleMatrixCells';
import { RoleMatrixToolbar } from '../RoleCoverage/RoleMatrixToolbar';
import type { CoverageMatrixVM } from '../RoleCoverage/useCoverageMatrix';
import styles from '../RoleCoverage/RoleMatrix.module.css';

/**
 * EAA-1817 (design frame 55a "Ma tran do phu"): the **Ma trận** view of "Liên
 * kết vai trò" — every standard role against every application in one grid.
 *
 * WHY IT EXISTS ALONGSIDE THE PER-APP BOARD. The board (frame 55) answers "what
 * is this ONE app missing" — it shows one app at a time by construction,
 * because the editor beneath it edits one app's chips. That is the right shape
 * for fixing a link and the wrong shape for the question the design's own
 * changelog quotes as the reason for this frame: "cần một view thấy ngay nhiều
 * vai trò × nhiều ứng dụng". A gap you cannot see is a gap nobody closes.
 *
 * WHY IT IS THE KIT `MatrixTable` AND NOT A LOCAL `<table>`. This screen HAD a
 * hand-rolled `rc-matrix` table (`CoverageMatrixCard`, deleted with this
 * change), justified at the time by the kit having no column pinning and no
 * per-cell escape hatch. Both landed since — `stickyColumns` and `renderCell` —
 * so the justification expired, and keeping a second sticky-offset
 * implementation would mean maintaining the arithmetic twice. Everything that
 * is genuinely this screen's own (the four cell states, the coverage bars, the
 * legend) is a node passed IN; nothing about the grid mechanics is local.
 *
 * ONE FLAT SECTION, DELIBERATELY. `MatrixTable` takes rows grouped into
 * sections; standard roles have no grouping in this design, so this passes one
 * section with no `label` — which the kit now reads as "draw the rows, not a
 * band". A band naming nothing above every role would be noise.
 *
 * SHIFT+CLICK MULTI-SELECT IS NOT IMPLEMENTED. The frame's footer hint offers
 * it ("Shift+bấm để chọn nhiều ô") but no frame draws what the selection then
 * lets you do — there is no bulk bar for the matrix anywhere in the pack, and
 * the list view's bulk actions operate on `RoleMap` ROWS, which an uncovered
 * cell does not have. Guessing a bulk surface here would be inventing product,
 * so the hint text ships describing single-click only and the multi-select is
 * recorded as outstanding in the SRS.
 */
export function RoleMapMatrixView({
    vm,
    canManage,
    selectedAppCode,
    onOpenCell,
}: {
    vm: CoverageMatrixVM;
    canManage: boolean;
    /** Tints that app's whole column — the app currently open in the editor. */
    selectedAppCode: string | null;
    /** Omitted for a read-only caller; see `RoleMatrixCell`. */
    onOpenCell?: (cell: MatrixCell) => void;
}) {
    const { t } = useTranslation();

    const columns = useMemo<MatrixColumn[]>(
        () =>
            vm.visibleColumns.map((column) => ({
                key: column.code,
                header: <RoleMatrixColumnHeader column={column} />,
                width: 141,
                minWidth: 110,
                maxWidth: 260,
                // A not-ready column has no cell anyone can act on, so it is
                // locked rather than merely styled — that keeps it out of the
                // tab order instead of offering focus to a dead affordance.
                locked: !column.ready,
                className:
                    (column.code === selectedAppCode ? `${styles.colSelected} ` : '') +
                    (column.ready ? '' : styles.colNotReady),
            })),
        [vm.visibleColumns, selectedAppCode]
    );

    const sections = useMemo<MatrixSection[]>(
        () => [
            {
                key: 'roles',
                // No `label` — one flat group; see the docstring.
                rows: vm.visibleRows.map((row) => ({
                    key: row.roleKey,
                    header: <RoleMatrixRowHeader row={row} />,
                    className: row.hasPaused ? styles.rowError : undefined,
                })),
            },
        ],
        [vm.visibleRows]
    );

    return (
        // EAA-1846: `data-testid` is the e2e hook now — `e2e/pages/RoleMapConsole.ts`
        // reaches the table through it, because the matrix's class names are
        // hashed by CSS Modules and `MatrixTable` has no testid seam of its own.
        <section
            className={`hz-card ${styles.card}`}
            data-testid="role-matrix"
            aria-label={t('console.roleCoverage.matrixView.ariaLabel')}
        >
            <RoleMatrixToolbar vm={vm} />

            <MatrixTable
                columns={columns}
                sections={sections}
                rowHeaderWidth={212}
                stickyColumns={0}
                /* Frame 55a pins the role column at 212px and each app column at
                   141px. With eight applications that fills 1440px exactly; with
                   two it does not, and the table's `min-width: 100%` would spread
                   the surplus across them (measured: 141 -> 360). A coverage
                   grid's columns have to stay the same width as applications are
                   added, so the surplus goes into a filler column instead. */
                fillTrailingSpace
                label={t('console.roleCoverage.matrixView.ariaLabel')}
                cornerHeader={
                    <span className={styles.cornerLabel}>
                        {t('console.roleCoverage.matrixView.rowAxisHeader')}
                    </span>
                }
                // The boolean model underneath is unused: every cell is one of
                // four NAMED states, not a tick, so `renderCell` owns the cell
                // and `isChecked` only ever feeds the `ctx` this ignores.
                isChecked={() => false}
                renderCell={({ row, column }) => {
                    const roleRow = vm.rowByKey.get(row.key);
                    const appColumn = vm.columnByCode.get(column.key);
                    if (!roleRow || !appColumn) return null;
                    const index = vm.allColumns.findIndex((c) => c.code === column.key);
                    const cell = roleRow.cells[index];
                    if (!cell) return null;
                    return (
                        <RoleMatrixCell
                            cell={cell}
                            column={appColumn}
                            row={roleRow}
                            onOpen={canManage ? onOpenCell : undefined}
                        />
                    );
                }}
                storageKey="role-matrix"
                /* Bumped to 2: v1 shipped before the trailing-filler column, so
                   any width a user had already dragged (or that the stretching
                   behaviour had persisted) survives into the new layout and
                   silently overrides the frame's 141px. A version bump is what
                   `MatrixTable` documents for exactly this. */
                storageVersion={2}
                emptyMessage={
                    vm.filtersActive
                        ? t('console.roleCoverage.matrixView.emptyFiltered')
                        : t('console.roleCoverage.matrixView.empty')
                }
                resizeLabel={(name) => t('console.roleCoverage.matrixView.resizeLabel', { name })}
                classNames={{
                    root: styles.root,
                    scroll: styles.scroll,
                    table: styles.matrix,
                    cornerHeader: styles.corner,
                    columnHeader: styles.colHead,
                    stickyColumn: 'rc-matrix-sticky',
                    rowHeader: styles.rowHead,
                    cell: styles.td,
                    empty: styles.empty,
                    resizeHandle: 'rc-matrix-resize',
                }}
            />

            <div className={styles.foot}>
                <span className="rc-matrix-foot__tally">
                    {t('console.roleCoverage.matrixView.tally', {
                        cellCount: vm.tallies.cellCount,
                        linkedCount: vm.tallies.linkedCount,
                        uncoveredCount: vm.tallies.uncoveredCount,
                        pausedCount: vm.tallies.pausedCount,
                    })}
                </span>
                <span className={styles.footHint}>
                    {t('console.roleCoverage.matrixView.footHint')}
                </span>
            </div>
        </section>
    );
}

export default RoleMapMatrixView;
